/* eslint-disable max-len, no-nested-ternary */
/**
 * シミュレーション（保存済みのコピー + 変更）の純粋関数
 *
 * - 保存済みの ShiftItem[] からセル単位の SimulationItem[] を作る
 * - 変更の有無・件数を数える
 * - 反映時に A3 の operations[] へ変換する
 */
import {
  DayHeader, Operation, SavedShift, ShiftItem, SimulationItem, SimulationShift,
} from './types';
import { workMinutesOf } from './time';

export const cellKey = (staffCode: string, date: string): string => `${staffCode}|${date}`;

export const fromShiftItem = (s: ShiftItem): SimulationShift => ({
  shiftId: s.shiftId,
  expectedUpdateDate: s.updateDate,
  attendType: s.attendType,
  isLegal: s.isLegal,
  shiftPatternId: s.shiftPatternId,
  shiftPatternShortName: s.shiftPatternShortName,
  color: s.color,
  start: s.start,
  end: s.end,
  isEndNextDay: s.isEndNextDay,
  breakMinutes: s.breakMinutes,
  workMinutes: s.workMinutes,
  businessId: s.mainBusinessId,
  workOrgCode: s.orgCode,
  memo: s.memo || '',
  support: null,
});

/** 保存済みのシフトからセル一覧を作る（自店で編集対象のものだけ） */
export const buildItemsFromProduction = (shifts: Array<ShiftItem>, viewOrgCode: string): Array<SimulationItem> => {
  const map: { [key: string]: SimulationItem } = {};
  shifts.forEach((s) => {
    if (s.orgCode !== viewOrgCode) {
      return;
    }
    const k = cellKey(s.staffCode, s.targetDate);
    if (!map[k]) {
      map[k] = { staffCode: s.staffCode, targetDate: s.targetDate, shifts: [] };
    }
    map[k].shifts.push(fromShiftItem(s));
  });
  return Object.keys(map).map((k) => map[k]);
};

const normalize = (s: SimulationShift): string => [
  s.attendType,
  s.attendType === 0 ? s.shiftPatternId || '' : '',
  s.attendType === 0 ? s.start || '' : '',
  s.attendType === 0 ? s.end || '' : '',
  s.attendType === 0 ? (s.isEndNextDay ? 1 : 0) : '',
  s.attendType === 0 ? s.businessId || '' : '',
  s.attendType === 0 ? (s.breakMinutes === null ? '' : s.breakMinutes) : '',
  s.attendType !== 0 ? (s.isLegal ? 1 : 0) : '',
  s.workOrgCode,
  s.memo || '',
].join('/');

/** セルが保存済みの内容と違うか */
export const isCellChanged = (production: Array<ShiftItem>, item: SimulationItem, viewOrgCode: string): boolean => {
  const base = production
    .filter((s) => s.orgCode === viewOrgCode && s.staffCode === item.staffCode && s.targetDate === item.targetDate)
    .map((s) => normalize(fromShiftItem(s)))
    .sort();
  const cur = item.shifts.filter((s) => !s.deleted).map(normalize).sort();
  return base.join('\n') !== cur.join('\n');
};

/** changed フラグを付け直し、件数を返す */
export const markChanged = (production: Array<ShiftItem>, items: Array<SimulationItem>, viewOrgCode: string): number => {
  let n = 0;
  items.forEach((it) => {
    const changed = isCellChanged(production, it, viewOrgCode);
    // eslint-disable-next-line no-param-reassign
    it.changed = changed;
    if (changed) {
      n += 1;
    }
  });
  return n;
};

/** 新規シフトの仮 ID。保存の応答（saved）と突き合わせるので、操作列を作るときと同じ規則で出す */
export const tempIdFor = (it: SimulationItem, s: SimulationShift, idx: number): string => s.tempId || `t-${it.staffCode}-${it.targetDate}-${idx}`;

/** 反映用の操作列。確定済みの日を含むかも返す */
export const toOperations = (
  production: Array<ShiftItem>, items: Array<SimulationItem>, days: Array<DayHeader>, viewOrgCode: string,
): { operations: Array<Operation>; confirmedDates: Array<string> } => {
  const deletes: Array<Operation> = [];
  const upserts: Array<Operation> = [];
  const confirmedDates: Array<string> = [];
  const confirmed: { [date: string]: boolean } = {};
  days.forEach((d) => {
    confirmed[d.date] = d.confirmed;
  });
  const prodById: { [id: string]: ShiftItem } = {};
  production.forEach((s) => {
    prodById[s.shiftId] = s;
  });

  items.forEach((it) => {
    if (!isCellChanged(production, it, viewOrgCode)) {
      return;
    }
    if (confirmed[it.targetDate] && confirmedDates.indexOf(it.targetDate) === -1) {
      confirmedDates.push(it.targetDate);
    }
    const seen: { [id: string]: boolean } = {};
    it.shifts.forEach((s, idx) => {
      if (s.shiftId) {
        seen[s.shiftId] = true;
      }
      if (s.deleted) {
        if (s.shiftId) {
          deletes.push({
 op: 'DELETE', shiftId: s.shiftId, expectedUpdateDate: s.expectedUpdateDate, targetDate: it.targetDate, staffCode: it.staffCode,
});
        }
        return;
      }
      const base: Operation = {
        op: s.shiftId ? 'UPDATE' : 'CREATE',
        tempId: s.shiftId ? undefined : tempIdFor(it, s, idx),
        shiftId: s.shiftId,
        expectedUpdateDate: s.expectedUpdateDate,
        staffCode: it.staffCode,
        workOrgCode: s.workOrgCode || viewOrgCode,
        targetDate: it.targetDate,
        attendType: s.attendType,
        isLegal: s.attendType === 1,
        shiftPatternId: s.shiftPatternId,
        start: s.attendType === 0 ? s.start : null,
        end: s.attendType === 0 ? s.end : null,
        isEndNextDay: s.isEndNextDay,
        breakMinutes: s.breakMinutes,
        businessId: s.businessId,
        sourceShiftId: s.sourceShiftId || null,
        memo: s.memo,
        support: s.support,
      };
      if (s.attendType !== 0) {
        base.op = s.shiftId ? 'UPDATE' : 'HOLIDAY';
      }
      // 保存済みと同じ内容の UPDATE は送らない
      if (s.shiftId && prodById[s.shiftId] && normalize(fromShiftItem(prodById[s.shiftId])) === normalize(s)) {
        return;
      }
      upserts.push(base);
    });
    // 保存済みにあってシミュレーションから消えたもの
    production
      .filter((p) => p.orgCode === viewOrgCode && p.staffCode === it.staffCode && p.targetDate === it.targetDate && !seen[p.shiftId])
      .forEach((p) => deletes.push({
 op: 'DELETE', shiftId: p.shiftId, expectedUpdateDate: p.updateDate, targetDate: p.targetDate, staffCode: p.staffCode,
}));
  });
  // 消す操作を先に送る（10/5）。A3 は 1 件ずつ同じ時間帯のシフトが無いかを確かめるので、
  // 消すものが後ろにあると、その手前の登録が「既にシフトが登録されています」で 400 になる
  return { operations: [...deletes, ...upserts], confirmedDates };
};

/**
 * 保存しているあいだに触られたセル（次の回に送る分）に、今の回で保存できたシフトの ID・更新日時を当て直す（10/5）
 *
 * 当て直さないと、保存前の仮のシフト（shiftId なし）のまま次の回に回り、同じシフトをもう一度 CREATE して
 * 保存済みの方を DELETE することになり、時間が重なって 400 になっていた。
 * 既存のシフトにも保存後の更新日時を付ける（続けて直したときに 409 にならないように）。
 * 送ったシフトと同じオブジェクト（セルを直すときに使い回される）だけを当て直す。
 */
export const rebaseOnSaved = (
  rest: Array<SimulationItem>, sentCells: Array<SimulationItem>, saved: Array<SavedShift>,
): Array<SimulationItem> => {
  const byTemp: { [tempId: string]: SavedShift } = {};
  const byId: { [shiftId: string]: SavedShift } = {};
  saved.forEach((ss) => {
    if (ss.tempId) {
      byTemp[ss.tempId] = ss;
    }
    byId[ss.shiftId] = ss;
  });
  const savedOf = new Map<SimulationShift, SavedShift>();
  sentCells.forEach((it) => {
    it.shifts.forEach((s, idx) => {
      const ss = s.shiftId ? byId[s.shiftId] : byTemp[tempIdFor(it, s, idx)];
      if (ss && !s.deleted) {
        savedOf.set(s, ss);
      }
    });
  });
  if (savedOf.size === 0) {
    return rest;
  }
  return rest.map((it) => ({
    ...it,
    shifts: it.shifts.map((s) => {
      const ss = savedOf.get(s);
      if (!ss) {
        return s;
      }
      return {
        ...s, tempId: undefined, shiftId: ss.shiftId, expectedUpdateDate: ss.updateDate,
      };
    }),
  }));
};

/** operations（自動作成・コピーの結果）をセルに積む */
let applySeq = 0;

export const applyOperations = (
  items: Array<SimulationItem>, ops: Array<Operation>, patternShortName: (id: string | null) => string | null,
  patternColor: (id: string | null) => string | null, patternTimes: (id: string | null) => { start: string; end: string; isEndNextDay: boolean } | null,
): Array<SimulationItem> => {
  // 前期間コピー・自動作成の仮 ID（c1・g1 など）は呼ぶたびに 1 から振られる。続けて実行して保存待ちが重なっても
  // 保存の応答と取り違えないよう、呼んだ回ごとに一意にする（10/5）
  applySeq += 1;
  const tempPrefix = `a${applySeq}-`;
  const map: { [key: string]: SimulationItem } = {};
  items.forEach((it) => {
    map[cellKey(it.staffCode, it.targetDate)] = { ...it, shifts: it.shifts.map((s) => ({ ...s })) };
  });
  ops.forEach((op) => {
    if (!op.staffCode || !op.targetDate) {
      return;
    }
    const k = cellKey(op.staffCode, op.targetDate);
    if (!map[k]) {
      map[k] = { staffCode: op.staffCode, targetDate: op.targetDate, shifts: [] };
    }
    const cell = map[k];
    if (op.op === 'DELETE') {
      cell.shifts.forEach((s) => {
        if (s.shiftId === op.shiftId) {
          // eslint-disable-next-line no-param-reassign
          s.deleted = true;
        }
      });
      return;
    }
    const times = op.start ? { start: op.start, end: op.end || op.start, isEndNextDay: !!op.isEndNextDay } : patternTimes(op.shiftPatternId || null);
    const shift: SimulationShift = {
      tempId: op.tempId ? `${tempPrefix}${op.tempId}` : undefined,
      attendType: op.attendType || 0,
      isLegal: op.isLegal,
      shiftPatternId: op.shiftPatternId || null,
      shiftPatternShortName: patternShortName(op.shiftPatternId || null),
      color: patternColor(op.shiftPatternId || null),
      start: times ? times.start : null,
      end: times ? times.end : null,
      isEndNextDay: times ? times.isEndNextDay : false,
      breakMinutes: op.breakMinutes === undefined ? null : op.breakMinutes,
      businessId: op.businessId || null,
      sourceShiftId: op.sourceShiftId || null,
      workOrgCode: op.workOrgCode || '',
      memo: op.memo || '',
      support: op.support || null,
    };
    shift.workMinutes = shift.attendType === 0 ? workMinutesOf(shift.start, shift.end, shift.isEndNextDay, shift.breakMinutes) : 0;
    cell.shifts = cell.shifts.filter((s) => !s.deleted || s.shiftId);
    // 前期間コピーの上書き（A9 の UPDATE）は、既存のシフトをその内容で置き換える（10/5）。
    // 以前は CREATE と同じく足していたため、セルが 2 件になるか、時間が重なって保存エラーになっていた
    if (op.op === 'UPDATE' && op.shiftId) {
      const idx = cell.shifts.findIndex((s) => s.shiftId === op.shiftId && !s.deleted);
      if (idx >= 0) {
        const before = cell.shifts[idx];
        cell.shifts[idx] = {
          ...shift,
          tempId: undefined,
          shiftId: before.shiftId,
          // 盤面の更新日時を使う。プレビューのあとに他の人が直していたら 409 → 読み直しになる
          expectedUpdateDate: before.expectedUpdateDate,
          memo: before.memo,
        };
        return;
      }
      // 置き換える相手が手元に無い（プレビューのあとに消した など）ときは、新しいシフトとして足す
    }
    cell.shifts.push(shift);
  });
  return Object.keys(map).map((k) => map[k]);
};

/** セルを取り出す（無ければ空） */
export const findCell = (items: Array<SimulationItem>, staffCode: string, date: string): SimulationItem => {
  const found = items.filter((it) => it.staffCode === staffCode && it.targetDate === date)[0];
  return found || { staffCode, targetDate: date, shifts: [] };
};

/** セルを置き換えた新しい配列 */
export const replaceCell = (items: Array<SimulationItem>, cell: SimulationItem): Array<SimulationItem> => {
  const rest = items.filter((it) => !(it.staffCode === cell.staffCode && it.targetDate === cell.targetDate));
  return [...rest, cell];
};

/**
 * 保存できたセルを保存済み（board.shifts）に当てた新しい配列を返す（9/30）
 *
 * 保存のたびに盤面を読み直すと画面がちらつくので、画面の内容を正とし、サーバからは ID と更新日時だけを受け取る。
 * 保存したシフトは勤務時間・人件費・交通費が未計算になる（確定して公開したときに計算する）ので、その状態にしておく。
 * 送らなかったシフト（保存済みと同じ内容）は保存済みの値をそのまま使う。
 */
export const applySavedCells = (
  production: Array<ShiftItem>, cells: Array<SimulationItem>, saved: Array<SavedShift>, viewOrgCode: string,
  belongOrgCodeOf: (staffCode: string) => string, patternNameOf: (patternId: string | null) => string | null,
): Array<ShiftItem> => {
  const byTemp: { [tempId: string]: SavedShift } = {};
  const byId: { [shiftId: string]: SavedShift } = {};
  saved.forEach((ss) => {
    if (ss.tempId) {
      byTemp[ss.tempId] = ss;
    }
    byId[ss.shiftId] = ss;
  });
  const prodById: { [shiftId: string]: ShiftItem } = {};
  production.forEach((p) => {
    prodById[p.shiftId] = p;
  });
  const keys: { [key: string]: boolean } = {};
  cells.forEach((it) => {
    keys[cellKey(it.staffCode, it.targetDate)] = true;
  });
  const rest = production.filter((p) => !(p.orgCode === viewOrgCode && keys[cellKey(p.staffCode, p.targetDate)]));
  const added: Array<ShiftItem> = [];
  cells.forEach((it) => {
    it.shifts.forEach((s, idx) => {
      if (s.deleted) {
        return;
      }
      const ss = s.shiftId ? byId[s.shiftId] : byTemp[tempIdFor(it, s, idx)];
      const prev = s.shiftId ? prodById[s.shiftId] : undefined;
      if (prev && !ss) {
        added.push(prev);
        return;
      }
      const shiftId = s.shiftId || (ss ? ss.shiftId : '');
      if (!shiftId) {
        return;
      }
      const work = s.attendType === 0;
      const samePattern = !!prev && prev.shiftPatternId === s.shiftPatternId;
      added.push({
        shiftId,
        staffCode: it.staffCode,
        orgCode: s.workOrgCode || viewOrgCode,
        belongOrgCode: prev ? prev.belongOrgCode : belongOrgCodeOf(it.staffCode),
        targetDate: it.targetDate,
        attendType: s.attendType,
        start: work ? s.start : null,
        end: work ? s.end : null,
        isEndNextDay: work ? s.isEndNextDay : false,
        shiftPatternId: work ? s.shiftPatternId : null,
        shiftPatternName: work ? patternNameOf(s.shiftPatternId) : null,
        shiftPatternShortName: work ? s.shiftPatternShortName || null : null,
        color: work ? s.color || null : null,
        mainBusinessId: s.businessId,
        mainBusinessName: prev && prev.mainBusinessId === s.businessId ? prev.mainBusinessName : null,
        details: samePattern && prev ? prev.details : [],
        holidayId: !work && prev ? prev.holidayId : null,
        holidayName: !work && prev ? prev.holidayName : null,
        isLegal: work ? null : s.attendType === 1,
        breakMinutes: s.breakMinutes as number,
        // 保存では勤務時間・人件費を計算しない。0 と null は「未計算」の印になる
        workMinutes: 0,
        memo: s.memo || '',
        supportRequest: prev ? prev.supportRequest : null,
        isAway: false,
        awayOrgName: null,
        alerts: prev ? prev.alerts : [],
        laborCost: null,
        transportation: null,
        locked: false,
        lockReason: null,
        updateDate: ss ? ss.updateDate : (s.expectedUpdateDate || null),
      });
    });
  });
  return [...rest, ...added];
};

/**
 * 保存待ちの編集を、保存済みの全セルに重ねた一覧にする（9/30）
 *
 * 保存待ち（items）はいつも全セルを持たせる。画面も操作列づくりも、items があるあいだは items だけを見るため、
 * 一部のセルだけの items にすると、無いセルが空に見え、そこへ追加すると既存のシフトを消す操作になってしまう。
 */
export const withProduction = (items: Array<SimulationItem>, production: Array<ShiftItem>, viewOrgCode: string): Array<SimulationItem> => {
  const own: { [key: string]: boolean } = {};
  items.forEach((it) => {
    own[cellKey(it.staffCode, it.targetDate)] = true;
  });
  const base = buildItemsFromProduction(production, viewOrgCode).filter((it) => !own[cellKey(it.staffCode, it.targetDate)]);
  return [...base, ...items];
};
